import { type FC, useRef, useState } from "react";

import { ErrorLine } from "@styled/error_line";
import { LinkButton } from "@styled/link_button";
import { NoticeLine } from "@styled/notice_line";
import { QuestionList } from "@styled/question_row";
import { SectionsHeader, SectionsPanel } from "@styled/sections_header";
import { TogglePill } from "@styled/toggle_pill";

import { DraftingFieldsGroup } from "./drafting_fields_group";
import { DraftingSectionEditor } from "./drafting_section_editor";
import {
  DraftingSectionItem,
  type SectionInChat,
} from "./drafting_section_item";
import { DraftingSetupReview } from "./drafting_setup_review";
import { useDraftingSectionsOf } from "./use_drafting_sections";
import { usePointing } from "./use_pointing";

// The case's Drafting sections on a Word draft, in full, each with its status
// in the chat open (`statuses`); the chevron closes them (`onClose`).
// "+ Add section" brings his report into view (`onShowReport`) to point at.
export const DraftingSectionsList: FC<{
  disabled: boolean;
  onAsk(question: string): void;
  statuses?: ReadonlyMap<string, SectionInChat>;
  onClose?(): void;
  onShowReport?(): void;
}> = ({ disabled, onAsk, statuses, onClose, onShowReport }) => {
  const drafting = useDraftingSectionsOf();
  const { setup } = drafting;
  const toggleRef = useRef<HTMLSpanElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const [keep] = useState([toggleRef, hintRef]);
  const [adding, setAdding] = useState<{ top: number; left: number } | null>(
    null,
  );
  const count = drafting.sections.length;
  // "+ Add section" asks for the spot in his report, as Insert here does.
  const [pointing, togglePointing] = usePointing(keep, count);
  if (!drafting.wordDraft) return null;

  const point = () => {
    if (!pointing) onShowReport?.();
    togglePointing();
  };
  // At the end, the keyboard's way and the one when his report can't show.
  const addAtEnd = () => {
    const box = hintRef.current?.getBoundingClientRect();
    togglePointing();
    if (box) setAdding({ top: box.bottom + 6, left: box.left });
  };

  return (
    <SectionsPanel>
      <SectionsHeader
        line={setup.proposal?.sectionsLine ?? drafting.sectionsLine}
        onClose={onClose}
        action={
          !setup.showing && (
            <span ref={toggleRef}>
              <TogglePill tone="indigo" pressed={pointing} onClick={point}>
                + Add section
              </TogglePill>
            </span>
          )
        }
      />
      {pointing && (
        <div ref={hintRef}>
          <NoticeLine>
            Point at a gap in your report, then click Insert section here. Or{" "}
            <LinkButton onClick={addAtEnd}>add it at the end</LinkButton>.
          </NoticeLine>
        </div>
      )}
      {!setup.showing && <DraftingFieldsGroup />}
      {setup.showing ? (
        <DraftingSetupReview />
      ) : count === 0 ? (
        <NoticeLine>
          No sections yet. Hover a gap in your report to add one, or use + Add
          section.
        </NoticeLine>
      ) : (
        <QuestionList>
          {drafting.sections.map((section) => (
            <DraftingSectionItem
              key={section.id}
              section={section}
              inChat={statuses?.get(section.id)}
              disabled={disabled}
              onAsk={onAsk}
            />
          ))}
        </QuestionList>
      )}
      {/* Under the list, so rows don't move under an open picker. */}
      {drafting.caseOnly && (
        <NoticeLine>
          Saved for this case only. Your template wasn't changed.
        </NoticeLine>
      )}
      {drafting.error && <ErrorLine>{drafting.error}</ErrorLine>}
      {adding && (
        <DraftingSectionEditor
          target={{ before: null }}
          top={adding.top}
          left={adding.left}
          onClose={() => setAdding(null)}
        />
      )}
    </SectionsPanel>
  );
};
